import type { CameraAction, CameraState } from '../../hooks/useCinematicCamera';
import type { RecordingControls } from '../../hooks/useRecordingMode';

interface Props {
  readonly camera: CameraState;
  readonly dispatch: (action: CameraAction) => void;
  readonly recording: RecordingControls;
}

interface Toggle {
  key: string;
  label: string;
  active: boolean;
  onClick: () => void;
  title: string;
  tone: string;
}

export function RecordBar({ camera, dispatch, recording }: Props) {
  const toggles: Toggle[] = [
    {
      key: 'R',
      label: recording.recording ? 'STOP' : 'REC',
      active: recording.recording,
      tone: 'var(--color-deck-alert)',
      title: 'Start/stop recording (MediaRecorder → .webm download)',
      onClick: () => {
        if (recording.recording) {
          recording.stop();
          dispatch({ type: 'SET_MODE', mode: 'free' });
        } else {
          void recording.start();
          dispatch({ type: 'SET_MODE', mode: 'record' });
        }
      },
    },
    {
      key: 'H',
      label: 'HUD',
      active: camera.hudVisible,
      tone: 'var(--color-deck-cyan)',
      title: 'Hide HUD for clean capture',
      onClick: () => dispatch({ type: 'TOGGLE_HUD' }),
    },
    {
      key: 'Z',
      label: camera.zoom === 'wide' ? 'WIDE' : camera.zoom === 'medium' ? 'MED' : 'CLOSE',
      active: camera.zoom !== 'wide',
      tone: 'var(--color-deck-cyan)',
      title: 'Cycle zoom',
      onClick: () => dispatch({ type: 'CYCLE_ZOOM' }),
    },
    {
      key: 'T',
      label: camera.theme === 'night' ? 'NIGHT' : 'DAY',
      active: camera.theme === 'night',
      tone: 'var(--color-deck-amber)',
      title: 'Toggle day/night theme',
      onClick: () => dispatch({ type: 'TOGGLE_THEME' }),
    },
  ];

  return (
    <nav
      aria-label="Recording controls"
      className="absolute z-10 pointer-events-auto enter"
      style={{
        top: 72,
        right: 28,
        display: 'flex',
        gap: 8,
        animationDelay: '0.15s',
      }}
    >
      {toggles.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={t.onClick}
          title={t.title}
          aria-pressed={t.active}
          aria-label={`${t.label} (keyboard: ${t.key})`}
          className={t.active ? 'pill pill--active' : 'pill'}
          style={{ color: t.tone, minWidth: 72 }}
        >
          <span style={{ opacity: t.active ? 0.65 : 0.5, marginRight: 6, fontSize: 9 }}>
            {t.key}
          </span>
          {t.label}
        </button>
      ))}
    </nav>
  );
}
